import { useState, useEffect, useMemo } from "react";

import { useRouter } from "next/router";

import { useTranslator } from "@eo-locale/react";

import { Contribution } from "@/storybook-components";
import { buildImgixUrl, dateWithSiteLocale, stripEditorHtml } from "@/lib";
import { usePrivateFileRecordImageUrl } from "@/hooks/use-private-file-display-url";
import { useAppSelector } from "@/redux/store/hooks";

import { getRandomColor } from "@/helpers/get-random-color";
import { categoryMap } from "@/helpers/categories";
import { getVotingStatus } from "@/helpers/ideas";
import useBudget from "@/hooks/useBudget";
import { Idea } from "@/types";

const ContributionCard = ({
  cardData,
  clickHandler,
  cardLoaded,
  actionList,
  manageVotes,
  manageBudgeting,
  handleComment,
  showCommentIcon = true
}) => {
  const { query } = useRouter();
  const { projectId, brainstormerId } = query;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { settings } = useAppSelector((state) => state.settings);
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const [data, setData] = useState({ ...cardData });

  const categories = useAppSelector((state) => state.brainstormers.categories);
  const catMap = categoryMap(categories);

  useEffect(() => {
    setData(cardData);
  }, [cardData]);

  const {
    id,
    author,
    createdAt,
    name,
    description,
    commentsCount,
    votesCount,
    currentUserVoted,
    avatar,
    backgroundColourId,
    userType,
    baItemConfig,
    currentUserAllocation,
    categoryId
  }: Idea = data || {};
  const { unitCap } = baItemConfig || {};

  const { translate, language } = useTranslator();
  const { moderated } = useAppSelector((state) => state?.site?.site);
  const { login: loggedInUser, type: loggedInUserType } = useAppSelector(
    (state) => state.user?.registeredUser?.profile
  );
  const { brainstormer, status, currentBAContributionId } = useAppSelector(
    (state) => state.brainstormers
  );
  const {
    state: brainstormerState,
    votingStartAt,
    votingEndAt,
    disableVoting,
    layout
  } = brainstormer;
  const { project } = useAppSelector((state) => state.project);
  const { state: projectState } = project;
  const { formatBudget } = useBudget();

  const isVotingAllowed =
    (votingStartAt &&
      votingEndAt &&
      getVotingStatus(votingStartAt, votingEndAt) === "active") ||
    getVotingStatus(votingStartAt, votingEndAt) === "ended";

  const isLoggedInUser = author === loggedInUser;
  const isMatchingUserType = userType === loggedInUserType;
  const hasActionOptions =
    moderated ||
    (isLoggedInUser &&
      isMatchingUserType &&
      !isVotingAllowed &&
      projectState !== "archived" &&
      brainstormerState !== "archived");
  const unitCode = brainstormer?.baCategoryConfigAttributes?.unitCode || "";

  const colorList = ["#975A16", "#2C7A7B", "#B83280", "#6B46C1"];
  const getCardColor = useMemo(() => getRandomColor(colorList), [id]);

  const totalFund = formatBudget(
    currentUserAllocation || unitCap || 0,
    unitCode
  );

  const categoryName =
    categoryId != null ? catMap[String(categoryId)] : undefined;

  const mediaImageUrl = usePrivateFileRecordImageUrl({
    record: data,
    tool: "ideas",
    height: 252,
    width: 448,
    fit: "min"
  });

  return (
    <Contribution
      id={id}
      onClick={clickHandler}
      variant="vertical"
      username={author || ""}
      contributionTime={
        dateWithSiteLocale(createdAt, language.toLowerCase()) || null
      }
      imageSrc={mediaImageUrl || null}
      imageAlt={name || ""}
      showImage
      cardHeading={name || ""}
      cardDescription={stripEditorHtml(description) || ""}
      showDescription
      userAvatarSrc={buildImgixUrl(avatar, 40, 40, "min") || null}
      showFooter
      showCommentIcon={showCommentIcon}
      handleComment={handleComment}
      commentsCount={commentsCount || 0}
      isThumbsUp={!disableVoting}
      thumbsUpCount={votesCount || 0}
      handleVoteUp={() => manageVotes(id)}
      shareText={translate("common.share") || ""}
      isLoaded={cardLoaded}
      showMoreOptions={hasActionOptions}
      actionList={actionList}
      isVoteActive={currentUserVoted}
      tileBackgroundColor={
        backgroundColourId
          ? backgroundColours.find(
              ({ id }) => `${backgroundColourId}` === `${id}`
            )?.name
          : getCardColor
      }
      showTags={!!categoryName}
      tags={categoryName ? [categoryName] : []}
      ariaLabelVoteIcon={`${translate("common.numberOfVotes", {
        votesCount
      })} ${
        currentUserVoted
          ? translate("common.removeVote")
          : translate("common.addVote")
      }`}
      ariaLabelCommentIcon={`${translate("common.numberOfComments", {
        commentsCount
      })} ${translate("common.addComment")}`}
      isIconButtonPressed={currentUserVoted}
      isShareIcon={!settings.disableSocialMediaSharing}
      ariaLabelShareIcon={translate("brainstormers.share")}
      socialShareTitle={name || ""}
      socialShareUrl={`${baseUrl}/${projectId}/brainstormers/${brainstormerId}?idea=${id}`}
      showBudgeting={layout === "BudgetAllocator"}
      totalFund={totalFund}
      fundLocale={translate("budget.fundFor")}
      fundedForLocale={translate("budget.fundedFor")}
      undoBudgetingLocale={translate("budget.undo")}
      hasTotalFunded={!!currentUserAllocation}
      handleBudgeting={() => manageBudgeting(id)}
      isBudgetUpdating={
        currentBAContributionId === id && status === "updatingUserAllocation"
      }
    />
  );
};

export default ContributionCard;
